import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, type Domain } from '../lib/api';
import s from './DomainsPage.module.css';

/* -------------------------------------------------------------------------- */
/*  Add Domain Modal                                                           */
/* -------------------------------------------------------------------------- */

interface CreatedDomain {
  id: string;
  domain: string;
  dnsRecord: string;
}

function AddDomainModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (d: Domain) => void;
}) {
  const [domainInput, setDomainInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedDomain | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const value = domainInput.trim().toLowerCase();
    if (!value) {
      setError('Enter a domain name.');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const result = await api.domains.create(value);
      const d = result.domain;
      const ruaAddress = `${d.id}@reports.dmarcopolo.com`;
      const dnsRecord = `_dmarc.${value} TXT "v=DMARC1; p=none; rua=mailto:${ruaAddress}"`;
      setCreated({ id: d.id, domain: d.domain, dnsRecord });
      onCreated(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  }

  function handleCopy() {
    if (!created) return;
    navigator.clipboard.writeText(created.dnsRecord).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={s.overlay} onClick={onClose}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalTitle}>Add Domain</div>

        {error && <div className={s.modalError}>{error}</div>}

        {!created ? (
          <form onSubmit={handleSubmit}>
            <div className={s.field}>
              <label htmlFor="new-domain" className={s.label}>
                Domain
              </label>
              <input
                id="new-domain"
                type="text"
                className={s.input}
                placeholder="example.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className={s.modalActions}>
              <button
                type="button"
                className={s.btnGhost}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={s.btnAccent}
                disabled={loading}
              >
                {loading ? 'Adding\u2026' : 'Add Domain'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className={s.successBanner}>
              Domain {created.domain} added successfully
            </div>

            <div className={s.label} style={{ marginBottom: 'var(--space-2)' }}>
              Publish this DMARC DNS record:
            </div>
            <div className={s.dnsRecord}>
              <button
                type="button"
                className={s.copyBtn}
                onClick={handleCopy}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
              {created.dnsRecord}
            </div>

            <div className={s.modalActions}>
              <button
                type="button"
                className={s.btnAccent}
                onClick={onClose}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Status helpers                                                             */
/* -------------------------------------------------------------------------- */

function statusDotClass(status: Domain['status']): string {
  switch (status) {
    case 'active':
      return s.dotActive;
    case 'pending':
      return s.dotPending;
    case 'error':
      return s.dotError;
    default:
      return s.dotInactive;
  }
}

/* -------------------------------------------------------------------------- */
/*  DomainsPage                                                                */
/* -------------------------------------------------------------------------- */

export default function DomainsPage() {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api.domains
      .list()
      .then(({ domains: list }) => {
        if (!cancelled) {
          setDomains(list);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load domains');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function handleCreated(d: Domain) {
    setDomains((prev) => [d, ...prev]);
  }

  if (loading) {
    return <div className={s.loading}>Loading domains&hellip;</div>;
  }

  if (error) {
    return <div className={s.error}>{error}</div>;
  }

  return (
    <>
      {/* Header */}
      <div className={s.header}>
        <h1 className={s.title}>Domains</h1>
        <button
          type="button"
          className={s.btnAccent}
          onClick={() => setShowModal(true)}
        >
          Add Domain
        </button>
      </div>

      {/* Domain list */}
      {domains.length === 0 ? (
        <div className={s.emptyState}>
          <span>No domains yet. Add your first domain to start monitoring.</span>
          <button
            type="button"
            className={s.btnAccent}
            onClick={() => setShowModal(true)}
          >
            Add Domain
          </button>
        </div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Domain</th>
                <th>Status</th>
                <th>Reports (7d)</th>
                <th>Pass Rate</th>
                <th>Last Report</th>
                <th>DNS Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link to={`/app/domains/${d.id}`} className={s.domainLink}>
                      {d.domain}
                    </Link>
                  </td>
                  <td>
                    <span className={`${s.statusDot} ${statusDotClass(d.status)}`} />
                    {d.status}
                  </td>
                  <td>&mdash;</td>
                  <td>&mdash;</td>
                  <td style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                    {d.lastReportAt
                      ? new Date(d.lastReportAt).toLocaleDateString()
                      : 'None'}
                  </td>
                  <td>
                    <div className={s.dnsIndicators}>
                      <span className={s.dnsTag}>
                        <span className={`${s.dnsTagDot} ${s.dnsMissing}`} />
                        SPF
                      </span>
                      <span className={s.dnsTag}>
                        <span className={`${s.dnsTagDot} ${s.dnsMissing}`} />
                        DKIM
                      </span>
                      <span className={s.dnsTag}>
                        <span className={`${s.dnsTagDot} ${s.dnsMissing}`} />
                        DMARC
                      </span>
                    </div>
                  </td>
                  <td>
                    <button type="button" className={s.actionBtn}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add domain modal */}
      {showModal && (
        <AddDomainModal
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
        />
      )}
    </>
  );
}
